/**
 * X のカードのタグの欄（pages/SharePage.tsx → components/ShareTagsCard.tsx）の純粋な部分。
 * 2026-09-30 console/share-tags（founder の決定・案 D: タグは作者が自分のソースの <head> に貼る。Kumo が </head> に差し込むのはやめる）。
 *
 * サーバー: GET /v1/projects/:projectId/environments/:env/share/tags（apps/server/src/modules/share/routes.ts の tagsFor →
 *   tags.ts selfHostedCardTags）。応答 { enabled, shareId, image, defaultImage, shareImageTemplate, tags, html, relay }。
 * 画面が使うのは `html`（<meta …> を改行でつないだ塊 = そのまま <head> に貼れる）と `image`（既定のカードの画像の URL = 見せる用）だけ。
 * WHY `enabled` を読まないか: `enabled` は「Kumo が差し込むか（share.tags.enabled）」で、案 D ではその差し込み自体を閉じた（Core）。
 *   タグそのものは images が ON なら tags が OFF でも返る（自前で貼る人のため・routes.ts のコメント）。画面は貼る用のタグを出すだけ
 * WHY 共有ごとのカード（?ks=）を出さないか: 静的なサイトでは共有 ID ごとに <head> を変えられない = 既定のカードのタグだけを見せる
 *
 * このファイルは import を持たない（node --test で型の除去だけで動かすため。featureToggle.ts と同じ）。
 */

/** サーバーの応答（tags.ts selfHostedCardTags の戻り値と同じ形。読まない項目も型には残す = 応答の形の記録） */
export interface ShareTagsResponse {
  enabled: boolean;
  shareId: string | null;
  image: string | null;
  defaultImage: string | null;
  shareImageTemplate: string | null;
  tags: Array<{ attr: 'name' | 'property'; key: string; content: string }>;
  html: string;
  relay: { from: string; upstream: string } | null;
}

/** 画面に出す分（応答の読み方を 1 か所に） */
export interface ShareTagsView {
  /** <head> に貼るタグの塊（前後の空行なし・空なら ''）。コードの塊で見せてコピーする */
  html: string;
  /** カードの画像の URL（twitter:image の中身）。画像が無い（固定の画像も描いた画像も無い）なら null = 見せない */
  image: string | null;
}

/**
 * 応答 → 画面に出す分。`html` は文字列でなければ ''（古い / 別のサーバーの応答でも画面を壊さない）。
 * `image` は https(s) の絶対 URL のときだけ出す（<img src> に出すので、相対や空は出さない）
 */
export function shareTagsView(res: Partial<ShareTagsResponse> | null | undefined): ShareTagsView {
  const html = typeof res?.html === 'string' ? res.html.trim() : '';
  const image = typeof res?.image === 'string' && /^https?:\/\//.test(res.image) ? res.image : null;
  return { html, image };
}

/**
 * 「画像の機能（shareImages）が OFF」= タグは出せない（routes.ts tagsFor: images が OFF で固定の画像も無ければ 404 share_images_disabled）。
 * このときだけ「ON にすると出ます」の案内にする。ほかのエラーは普通のエラー表示
 */
export function isShareImagesOff(err: { status: number; code: string } | null | undefined): boolean {
  return !!err && err.status === 404 && err.code === 'share_images_disabled';
}

/**
 * AI エージェントがタグを取るコマンド（Creator と合意: `<cli> share tags`・何度打っても何も変えない読むだけの口）。
 * WHY 名前を引数にするか: コマンド名は cliCommand.ts の CLI_NAME の 1 か所（直書きしない・同時に動く console/kumodeck が名前を変える）。
 *   画面は shareTagsCommand(CLI_NAME) で作る。テストは仮の名前で形だけ確かめる（名前が変わってもここは変えない）
 */
export function shareTagsCommand(cli: string): string {
  return `${cli} share tags`;
}
